import { useMemo } from 'react';

import { MONTH_NAMES } from '../format';
import { WheelBox } from './TimeWheel';
import { Column } from './Wheel';

/**
 * C-011 (`FND-1604`, 2026-10-07, after the cutover): the day a song was sung, on the wheels the rest
 * of the app picks a date with. Spec: `docs/specs/features/C-011/spec.md` §2.2.
 *
 * The first stand build drew the browser's own date field here, and the owner asked for the app's
 * wheel instead on 2026-10-07. Three columns — number, month, year — as on «Профиль», and not the
 * one column of real dates «Когда» and a `once` sending scroll: those run forward from today, and a
 * day sung years ago would sit a thousand rows behind it.
 *
 * The columns stop at today: a later month of this year and a later day of this month are not in
 * them, so the server's `sung_date_ahead` stays a guard rather than something anybody meets. The
 * day column is as long as the month, so 31 April cannot be reached either.
 *
 * `null` is a day nobody has picked, and the wheels stand on today until one is: the button under
 * them is dead until a value is reported, as under `TimeWheel`, so drawing the sheet adds nothing.
 */
export default function SungDayWheel({
  value,
  onChange,
}: {
  /** «YYYY-MM-DD», or null for a day nobody has picked. */
  value: string | null;
  onChange: (value: string) => void;
}) {
  // Read once, while the sheet is open: a midnight under a finger would move the columns.
  const today = useMemo(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
  }, []);
  const years = useMemo(
    () => Array.from({ length: YEARS_BACK + 1 }, (_, row) => String(today.year - YEARS_BACK + row)),
    [today],
  );

  const [year, month, day] = parts(value) ?? [today.year, today.month, today.day];
  const months = year === today.year ? today.month : 12;
  const days =
    year === today.year && month === today.month ? today.day : new Date(year, month, 0).getDate();

  /** Clamped to today and to the month's length, and swallowed when it is the day already shown —
   *  the wheel reports itself again after every scroll it is sent on. */
  function set(nextYear: number, nextMonth: number, nextDay: number): void {
    const lastMonth = nextYear === today.year ? today.month : 12;
    const m = Math.min(nextMonth, lastMonth);
    const lastDay =
      nextYear === today.year && m === today.month ? today.day : new Date(nextYear, m, 0).getDate();
    const next = `${nextYear}-${pad(m)}-${pad(Math.min(nextDay, lastDay))}`;
    if (next === `${year}-${pad(month)}-${pad(day)}`) return;
    onChange(next);
  }

  return (
    <WheelBox>
      <Column
        label="День: число"
        items={DAY_LABELS.slice(0, days)}
        index={Math.min(day, days) - 1}
        onPick={(row) => set(year, month, row + 1)}
        mono
      />
      <Column
        label="День: месяц"
        items={MONTH_NAMES.slice(0, months)}
        index={Math.min(month, months) - 1}
        onPick={(row) => set(year, row + 1, day)}
        wide
      />
      <Column
        label="День: год"
        items={years}
        index={Math.max(0, year - (today.year - YEARS_BACK))}
        onPick={(row) => set(today.year - YEARS_BACK + row, month, day)}
        mono
      />
    </WheelBox>
  );
}

/** How many years before this one the year column holds. The repertoire was entered after years of
 *  singing; thirty is past anything a regent will remember a date from. */
const YEARS_BACK = 30;

const DAY_LABELS = Array.from({ length: 31 }, (_, index) => String(index + 1));

function parts(value: string | null): [number, number, number] | null {
  const said = value === null ? null : /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return said === null ? null : [Number(said[1]), Number(said[2]), Number(said[3])];
}

function pad(figure: number): string {
  return String(figure).padStart(2, '0');
}
